import axios from 'axios';
import type { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import type { AuthTokens } from '@/types';
import { secureTokenStorage } from './secureTokenStorage';
import { csrfService } from './csrfService';
import { secureLogger } from '@/utils/secureLogger';

// Define APIResponse locally to avoid circular dependency issues
interface APIResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
  timestamp?: string;
  status?: number;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

const createRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

class ApiService {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token and CSRF protection
    this.instance.interceptors.request.use(
      async (config) => {
        // SECURITY: Get valid access token from secure storage
        const token = await secureTokenStorage.getValidAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add/propagate request ID for traceability
        if (!config.headers['X-Request-Id']) {
          config.headers['X-Request-Id'] = createRequestId();
        }
        
        // SECURITY: Add CSRF headers for state-changing requests
        if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase() || '')) {
          const csrfHeaders = await csrfService.getCSRFHeaders();
          Object.assign(config.headers, csrfHeaders);
        }
        
        // Ensure credentials are included for HttpOnly cookies
        config.withCredentials = true;
        
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle authentication errors
    this.instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // Handle authentication errors
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // SECURITY: Use secure token storage for refresh
            const newToken = await secureTokenStorage.getValidAccessToken();
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.instance(originalRequest);
            } else {
              // No valid token available, redirect to login
              this.handleAuthError();
            }
          } catch (refreshError) {
            this.handleAuthError();
            return Promise.reject(refreshError);
          }
        }

        // Handle CSRF token errors
        if (error.response?.status === 403 && 
            (error.response?.data as any)?.message?.includes('CSRF') && 
            !originalRequest._csrfRetry) {
          originalRequest._csrfRetry = true;

          try {
            if (import.meta.env.DEV) {
              secureLogger.debug('🔄 CSRF error detected, refreshing token...');
            }
            // Refresh CSRF token
            await csrfService.refreshCSRFToken();
            
            // Get fresh CSRF headers
            const csrfHeaders = await csrfService.getCSRFHeaders();
            Object.assign(originalRequest.headers, csrfHeaders);
            
            return this.instance(originalRequest);
          } catch (csrfError) {
            secureLogger.error('❌ Failed to refresh CSRF token:', csrfError);
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // DEPRECATED: Legacy method for backward compatibility
  private getAccessToken(): string | null {
    secureLogger.warn('⚠️ getAccessToken() is deprecated. Using secure token storage.');
    return secureTokenStorage.getCurrentToken();
  }

  // DEPRECATED: Token storage is now handled by SecureTokenStorage
  private setTokens(tokens: AuthTokens): void {
    secureLogger.warn('⚠️ setTokens() is deprecated. Using secure token storage.');
    secureTokenStorage.setAccessToken(tokens.access_token, tokens.expires_in);
  }

  // DEPRECATED: Token clearing is now handled by SecureTokenStorage
  private clearTokens(): void {
    secureLogger.warn('⚠️ clearTokens() is deprecated. Using secure token storage.');
    secureTokenStorage.clearTokens();
  }

  private handleAuthError(): void {
    this.clearTokens();
    window.location.href = '/login';
  }

  private sanitizeDataForLogging(data: any): any {
    const sensitiveKeys = ['password', 'token', 'secret', 'apiKey', 'accessToken', 'refreshToken', 'access_token', 'refresh_token'];
    const redacted = '***REDACTED***';

    const replacer = (key: string, value: any) => {
      if (typeof key === 'string' && sensitiveKeys.some(sensitiveKey => key.toLowerCase().includes(sensitiveKey))) {
        return redacted;
      }
      return value;
    };

    try {
      // Use JSON.stringify with a replacer and then parse back to deep clone and sanitize
      return JSON.parse(JSON.stringify(data, replacer));
    } catch (error) {
      // Fallback for circular structures or other stringify errors
      return { error: 'Could not sanitize data for logging.' };
    }
  }

  // Public API methods
  public async get<T>(url: string, params?: any, config?: { timeout?: number }): Promise<APIResponse<T>> {
    try {
      const axiosConfig = {
        params,
        ...(config?.timeout && { timeout: config.timeout })
      };
      const response: AxiosResponse<APIResponse<T>> = await this.instance.get(url, axiosConfig);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async post<T>(url: string, data?: any, config?: { timeout?: number }): Promise<APIResponse<T>> {
    try {
      if (import.meta.env.DEV) {
        // Sanitize sensitive data before logging
        const sanitizedData = this.sanitizeDataForLogging(data);
        secureLogger.debug(`📤 API POST ${url}`, sanitizedData);
      }
      const axiosConfig = config?.timeout ? { timeout: config.timeout } : undefined;
      const response: AxiosResponse<APIResponse<T>> = await this.instance.post(url, data, axiosConfig);
      if (import.meta.env.DEV) {
        const sanitizedResponse = this.sanitizeDataForLogging(response.data);
        secureLogger.debug(`📥 API POST ${url} response:`, sanitizedResponse);
      }
      return response.data;
    } catch (error) {
      // Only log error type, not full error details in production
      if (import.meta.env.DEV) {
        secureLogger.error(`❌ API POST ${url} error:`, error);
      } else {
        secureLogger.error(`❌ API POST ${url} failed`);
      }
      throw this.handleError(error);
    }
  }

  public async put<T>(url: string, data?: any): Promise<APIResponse<T>> {
    try {
      const response: AxiosResponse<APIResponse<T>> = await this.instance.put(url, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async patch<T>(url: string, data?: any): Promise<APIResponse<T>> {
    try {
      const response: AxiosResponse<APIResponse<T>> = await this.instance.patch(url, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async delete<T>(url: string): Promise<APIResponse<T>> {
    try {
      const response: AxiosResponse<APIResponse<T>> = await this.instance.delete(url);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async upload<T>(url: string, formData: FormData, onProgress?: (progress: number) => void): Promise<APIResponse<T>> {
    try {
      const response: AxiosResponse<APIResponse<T>> = await this.instance.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const response = error.response;
      
      // Prefer backend-provided error/message when present
      if (response?.data?.message) {
        return new Error(response.data.message);
      }
      if (response?.data?.error) {
        return new Error(response.data.error);
      }
      
      if (response?.status) {
        switch (response.status) {
          case 400:
            return new Error('Bad request. Please check your input.');
          case 401:
            return new Error('Authentication required. Please log in.');
          case 403:
            return new Error('Access denied. You don\'t have permission to perform this action.');
          case 404:
            return new Error('Resource not found.');
          case 409:
            return new Error('Conflict. The resource already exists or there\'s a conflict.');
          case 422:
            return new Error('Validation failed. Please check your input.');
          case 429:
            return new Error('Too many requests. Please try again later.');
          case 500:
            return new Error('Internal server error. Please try again later.');
          case 503:
            return new Error('Service unavailable. Please try again later.');
          default:
            return new Error(`Request failed with status ${response.status}`);
        }
      }
      
      if (error.code === 'ECONNABORTED') {
        return new Error('Request timeout. Please try again.');
      }
      
      if (error.message === 'Network Error') {
        return new Error('Network error. Please check your connection.');
      }
    }

    return new Error(error.message || 'An unexpected error occurred');
  }

  public isAuthenticated(): boolean {
    const token = this.getAccessToken();
    
    if (!token) {
      return false;
    }

    // Check token expiration from decoded JWT (no localStorage leak)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  public logout(): void {
    this.clearTokens();
  }

  public setAuthTokens(tokens: AuthTokens): void {
    this.setTokens(tokens);
  }

  public getApiUrl(): string {
    return API_BASE_URL;
  }

  public getAxiosInstance(): AxiosInstance {
    return this.instance;
  }
}

export const apiService = new ApiService();
export default apiService;
